import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import api from '../../lib/api';
import { MergeDuplicatesDialog } from './MergeDuplicatesDialog';

// V63.7 — one button for "one mother, one account". New sign-ins can no longer create a
// second row (V63.1), but numbers written by the old code can still be split across two
// accounts. This repairs the stored formats first — including the `+9191…` double-country-code
// rows, which used to HIDE a duplicate from the matcher — then shows what is left to merge.
export const DuplicateAccountsCard = () => {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const refresh = useCallback(async (opts = {}) => {
    setBusy(true);
    try {
      let normalized = null;
      if (opts.repair) {
        const { data } = await api.post('/api/admin/users/normalize-phones?apply=true');
        normalized = data?.normalized ?? 0;
      }
      const { data: dup } = await api.get('/api/admin/users/duplicates');
      setScan({ total: dup?.total || 0, groups: dup?.groups || [], normalized });
      if (opts.repair) {
        toast.success(normalized
          ? `${normalized} phone number${normalized === 1 ? '' : 's'} cleaned up`
          : 'All phone numbers were already clean');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not check for duplicate accounts');
    }
    setBusy(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const phoneGroups = (scan?.groups || []).filter((g) => g.match_reason === 'phone');

  return (
    <Card data-testid="duplicate-accounts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Duplicate accounts (same phone number)
        </CardTitle>
        <CardDescription>
          Repairs how numbers are stored, then lists anyone who ended up with two accounts.
          Merging keeps the paid account and moves her batches, courses and entitlements onto it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => refresh({ repair: true })} disabled={busy}
            data-testid="duplicate-fix-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Fix phone numbers &amp; find duplicates
          </Button>
          <Button variant="outline" onClick={() => refresh()} disabled={busy}
            data-testid="duplicate-rescan-btn">Re-check</Button>
          {scan?.total > 0 && (
            <Button variant="outline" onClick={() => setMergeOpen(true)}
              data-testid="duplicate-open-merge-btn">Review &amp; merge</Button>
          )}
        </div>

        {scan && (
          <div className="text-sm" data-testid="duplicate-accounts-result">
            {scan.total === 0 ? (
              <p className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> No duplicate accounts — every learner is a single record.
              </p>
            ) : (
              <>
                <p className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  <span>
                    <strong>{scan.total}</strong> {scan.total === 1 ? 'person has' : 'people have'} more than one
                    account{phoneGroups.length ? ` · ${phoneGroups.length} by phone number` : ''}.
                  </span>
                </p>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {(scan.groups || []).slice(0, 6).map((g, i) => (
                    <li key={i} data-testid={`duplicate-group-row-${i}`}>
                      {g.shared_phone || g.shared_email || g.match_reason} — {(g.members || []).length} accounts
                      {g.resulting_tier ? ` · keeps ${g.resulting_tier}` : ''}
                    </li>
                  ))}
                  {scan.total > 6 && <li>…and {scan.total - 6} more — open “Review &amp; merge”.</li>}
                </ul>
              </>
            )}
            {scan.normalized !== null && scan.normalized !== undefined && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Last repair rewrote {scan.normalized} number{scan.normalized === 1 ? '' : 's'} to +91 format.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <MergeDuplicatesDialog open={mergeOpen} onClose={() => setMergeOpen(false)}
        onMerged={() => refresh()} />
    </Card>
  );
};

export default DuplicateAccountsCard;
